import React, {useMemo, useState} from 'react';
import {useApolloClient, useQuery} from "@apollo/client";
import {useNavigate} from "react-router-dom";
import {useKeycloak} from "@react-keycloak/web";
import {jwtDecode} from "jwt-decode";
import {AssetAssetLocation} from "../../graphql/queries/assetAssetLocation";
import {InstalledBaseNodeEventContext} from "../../graphql/queries/installedBaseNodeEventContext";
import {InstalledBaseNode, ManualEventType} from "../../codegen/graphql";
import _ from "lodash"
import {Box, Menu, MenuItem, ListSubheader} from "@mui/material";
import {IconButton} from "@mui/joy";
import {MoreVertRounded, LinkOffRounded, EditRounded, NotificationsActiveRounded, NotificationAddRounded, ArrowRightAltRounded, AddRounded} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import CreateInstalledBaseEventModal from "../CreateInstalledBaseEventModal";
import MoveInstalledBaseNodeModal from "../MoveInstalledBaseNodeModal";
import EditNodeModal from "../EditNodeModal";
import DetachIoTConnectionModal from "../DetachIoTConnectionModal";
import NodeFavoriteButton from "../NodeFavoriteButton";
import NodeSubscriptionModal from "../NodeSubscriptionModal";
import {InstalledBaseNodeSubscriptions} from "../../graphql/queries/installedBaseNodeSubscriptions";
import {filterAvailableEvents} from "../../utils/filterAvailableEvents";
import {useEventTypeLabels} from "../../utils/eventTypeLabels";
import {getEventTypeIcon} from "../../utils/eventTypeIcons";
import {useSubAssetCreation} from "../../hooks/useSubAssetCreation";

interface Props {
    assetLocationId: string
    installedBaseNodeId?: string
}

type MenuEntry = {
    key: string
    label: React.ReactNode
    onClick: () => void
    disabled?: boolean
}

type MenuCategory = {
    key: string
    label: string
    entries: MenuEntry[]
}

const AssetDashboardActions = ({assetLocationId, installedBaseNodeId}: Props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const client = useApolloClient();
    const {keycloak} = useKeycloak();
    const eventTypeLabels = useEventTypeLabels();

    const isIoTAdmin = useMemo(() => {
        if (!keycloak.token) return false;
        try {
            const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
            const roles = _.get(decoded, 'otto_access.roles', []) as string[];
            return roles.some(r => r === 'globalAdmin' || r === 'regionAdmin');
        } catch { return false; }
    }, [keycloak.token]);

    const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
    const [selectedEventType, setSelectedEventType] = useState<ManualEventType | undefined>(undefined);
    const [detachModalOpen, setDetachModalOpen] = useState(false);
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [editNodeModalOpen, setEditNodeModalOpen] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    const { data: subscriptionsData } = useQuery(InstalledBaseNodeSubscriptions, {
        fetchPolicy: 'cache-first',
        skip: !installedBaseNodeId,
    });

    const isAlreadySubscribed = useMemo(() => {
        if (!installedBaseNodeId) return false;
        const subs = (subscriptionsData?.installedBaseNodeSubscriptionList?.subscriptions ?? []) as any[];
        return subs.some((sub) => sub?.installedBaseNode?.id === installedBaseNodeId);
    }, [subscriptionsData, installedBaseNodeId]);

    const assetQuery = useQuery(AssetAssetLocation, {
        variables: {
            id: assetLocationId,
        }
    })
    const assetId = _.get(assetQuery, 'data.assetLocation.asset.id', null)
    const isStarred: boolean = _.get(assetQuery, 'data.assetLocation.installedBaseNode.isStarred', false)
    const iotConnectionId: string | null = _.get(assetQuery, 'data.assetLocation.iotConnection.id', null);

    const eventContextQuery = useQuery(InstalledBaseNodeEventContext, {
        variables: { id: installedBaseNodeId },
        skip: !installedBaseNodeId,
    });
    const availableEvents: ManualEventType[] = filterAvailableEvents(
        _.get(eventContextQuery, 'data.installedBaseNode.availableInstalledBaseEvents', []),
        _.get(eventContextQuery, 'data.installedBaseNode.assetLocation.isInstalled'),
    );
    const canWriteNode: boolean = _.get(eventContextQuery, 'data.installedBaseNode.access.write', false);
    const editNode = _.get(eventContextQuery, 'data.installedBaseNode') as InstalledBaseNode | undefined;

    const subAssetCreation = useSubAssetCreation(installedBaseNodeId);

    const handleEventClick = (eventType: ManualEventType) => {
        setSelectedEventType(eventType);
        setCreateEventModalOpen(true);
    };

    const categories: MenuCategory[] = useMemo(() => {
        const result: MenuCategory[] = [
            {
                key: 'events',
                label: t('Events'),
                entries: availableEvents.map((eventType) => ({
                    key: eventType,
                    label: (
                        <Box display="flex" alignItems="center">
                            <Box sx={{display: 'flex', alignItems: 'center', mr: '4px'}}>
                                {getEventTypeIcon(eventType, {fontSize: 'inherit', color: 'inherit'})}
                            </Box>
                            {eventTypeLabels[eventType]}
                        </Box>
                    ),
                    onClick: () => handleEventClick(eventType),
                })),
            },
            {
                key: 'actions',
                label: t('Actions'),
                entries: [
                    ...(subAssetCreation.canOffer ? [{
                        key: 'createSubAsset',
                        label: (
                            <Box>
                                <Box display="flex" alignItems="center">
                                    <Box sx={{display: 'flex', alignItems: 'center', mr: '4px'}}>
                                        <AddRounded fontSize="inherit" color="inherit"/>
                                    </Box>
                                    {t('Create Sub-Asset')}
                                </Box>
                                {/* A disabled MenuItem swallows hover, so the reason is shown inline. */}
                                {subAssetCreation.disabledReason && (
                                    <Box sx={{fontSize: '0.75rem', color: 'text.secondary', pl: '18px'}}>
                                        {subAssetCreation.disabledReason}
                                    </Box>
                                )}
                            </Box>
                        ),
                        onClick: subAssetCreation.open,
                        disabled: subAssetCreation.disabled,
                    }] : []),
                    ...((installedBaseNodeId && canWriteNode)
                    ? [{
                        key: 'edit',
                        label: (
                            <Box display="flex" alignItems="center">
                                <Box sx={{display: 'flex', alignItems: 'center', mr: '4px'}}>
                                    <EditRounded fontSize="inherit" color="inherit"/>
                                </Box>
                                {t('Edit asset')}
                            </Box>
                        ),
                        onClick: () => setEditNodeModalOpen(true),
                    },
                    // Moves the asset's installedBaseNode (NOT the assetLocation); the modal
                    // handles the target pick, the move preview and the lifecycle forms.
                    {
                        key: 'move',
                        label: (
                            <Box display="flex" alignItems="center">
                                <Box sx={{display: 'flex', alignItems: 'center', mr: '4px'}}>
                                    <ArrowRightAltRounded fontSize="inherit" color="inherit"/>
                                </Box>
                                {t('Move asset')}
                            </Box>
                        ),
                        onClick: () => setMoveModalOpen(true),
                    }]
                    : []),
                ],
            },
            {
                key: 'iot',
                label: t('IoT Connection'),
                entries: iotConnectionId
                    ? [
                        // Editing reuses the connection wizard, which is IoT-admin only
                        ...(isIoTAdmin ? [{
                            key: 'edit',
                            label: (
                                <Box display="flex" alignItems="center">
                                    <Box sx={{display: 'flex', alignItems: 'center', mr: '4px'}}>
                                        <EditRounded fontSize="inherit" color="inherit"/>
                                    </Box>
                                    {t('Edit connection')}
                                </Box>
                            ),
                            onClick: () => navigate(`/assets/${assetLocationId}/connect/edit`),
                        }] : []),
                        {
                            key: 'detach',
                            label: (
                                <Box display="flex" alignItems="center">
                                    <Box sx={{display: 'flex', alignItems: 'center', mr: '4px'}}>
                                        <LinkOffRounded fontSize="inherit" color="inherit"/>
                                    </Box>
                                    {t('Detach connection')}
                                </Box>
                            ),
                            onClick: () => setDetachModalOpen(true),
                        },
                    ]
                    : [],
            },
        ];
        return result.filter((c) => c.entries.length > 0);
    }, [availableEvents, eventTypeLabels, iotConnectionId, installedBaseNodeId, canWriteNode, isIoTAdmin, assetLocationId, navigate, t,
        subAssetCreation.canOffer, subAssetCreation.disabled, subAssetCreation.disabledReason, subAssetCreation.open]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    return (
        <Box display="flex" alignItems="center">
            <NodeFavoriteButton
                nodeId={installedBaseNodeId ?? _.get(assetQuery, 'data.assetLocation.installedBaseNode.id')}
                isStarred={isStarred}
                variant="outlined"
                sx={{mr: '5px', flexShrink: 0}}
            />
            {installedBaseNodeId && (
                <IconButton
                    variant="outlined"
                    color="neutral"
                    sx={{mr: '5px', flexShrink: 0}}
                    aria-label={isAlreadySubscribed
                        ? t('Edit notification subscription')
                        : t('Subscribe to notifications')
                    }
                    title={isAlreadySubscribed
                        ? t('Edit notification subscription')
                        : t('Subscribe to notifications')
                    }
                    onClick={() => setSubscriptionModalOpen(true)}
                >
                    {isAlreadySubscribed
                        ? <NotificationsActiveRounded/>
                        : <NotificationAddRounded/>
                    }
                </IconButton>
            )}
            {categories.length > 0 && (
                <>
                    <IconButton
                        variant="outlined"
                        aria-controls={menuOpen ? 'asset-dashboard-actions-menu' : undefined}
                        aria-expanded={menuOpen ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleMenuOpen}
                    >
                        <MoreVertRounded/>
                    </IconButton>
                    <Menu
                        id="asset-dashboard-actions-menu"
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                        transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    >
                        {categories.flatMap((category) => [
                            <ListSubheader
                                key={`${category.key}-header`}
                                disableSticky
                                sx={{
                                    bgcolor: 'grey.100',
                                    color: 'text.secondary',
                                    lineHeight: '28px',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                }}
                            >
                                {category.label}
                            </ListSubheader>,
                            ...category.entries.map((entry) => (
                                <MenuItem
                                    key={`${category.key}-${entry.key}`}
                                    disableRipple
                                    disabled={entry.disabled}
                                    onClick={() => {
                                        entry.onClick();
                                        handleMenuClose();
                                    }}
                                >
                                    {entry.label}
                                </MenuItem>
                            )),
                        ])}
                    </Menu>
                </>
            )}
            {createEventModalOpen && assetId &&
                <CreateInstalledBaseEventModal
                    assetId={assetId}
                    assetLocationId={assetLocationId}
                    installedBaseNodeId={installedBaseNodeId ?? null}
                    open={createEventModalOpen}
                    onClose={() => {
                        setCreateEventModalOpen(false);
                        setSelectedEventType(undefined);
                    }}
                    initialEventType={selectedEventType}
                />
            }
            {moveModalOpen && installedBaseNodeId &&
                <MoveInstalledBaseNodeModal
                    open={moveModalOpen}
                    onClose={() => setMoveModalOpen(false)}
                    nodeId={installedBaseNodeId}
                    assetId={assetId ?? undefined}
                    assetLocationId={assetLocationId}
                    // A same-client move reuses the AssetLocation (same id), so the open
                    // asset tab stays valid — refreshing its path/nameplate/events suffices.
                    refetchQueries={["AssetBreadcrumbPath", "AssetNameplateInfo", "InstalledBaseEventList", "InstalledBaseNodeEventContext"]}
                />
            }
            {editNodeModalOpen && editNode &&
                <EditNodeModal
                    open={editNodeModalOpen}
                    onClose={() => setEditNodeModalOpen(false)}
                    node={editNode}
                    refetchFunction={() => {
                        client.refetchQueries({
                            include: ["AssetBreadcrumbPath", "AssetNameplateBasicInfo", "AssetDashboardHero", "AssetSpecifications", "InstalledBaseNodeEventContext"],
                        });
                    }}
                />
            }
            {detachModalOpen && iotConnectionId &&
                <DetachIoTConnectionModal
                    open={detachModalOpen}
                    onClose={() => setDetachModalOpen(false)}
                    assetLocationId={assetLocationId}
                    iotConnectionId={iotConnectionId}
                />
            }
            {subAssetCreation.modal}
            {subscriptionModalOpen && installedBaseNodeId &&
                <NodeSubscriptionModal
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    installedBaseNodeId={installedBaseNodeId}
                />
            }
        </Box>
    );
}

export default AssetDashboardActions;
